import http from '../shared/HttpCommon';
import { Dispatch, SetStateAction } from 'react';
import { IPlatform } from '../shared/interfaces/IPlatform';
import { CanceledError } from 'axios';

let cachedPlatforms: IPlatform[] | null = null;
let cachedPromise: Promise<IPlatform[]> | null = null;

const getPlatforms = async (loading?: Dispatch<SetStateAction<boolean>>, signal?: AbortSignal) => {
  if (cachedPlatforms) return cachedPlatforms;
  if (cachedPromise) return cachedPromise;

  if (!loading) loading = (v) => true;
  if (!signal) signal = new AbortController().signal;

  try {
    loading(true);
    const promise = http.get('/platforms', { signal });
    cachedPromise = promise.then(({ data }) => data);
    const { data } = await promise;
    cachedPlatforms = data;
    loading(false);
    return data;
  } catch (error) {
    if (error instanceof CanceledError) {
    } else {
      loading(false);
      console.error(error);
    }
  }
};

const getPlatformId = (label: string, platforms: IPlatform[]) => {
  return platforms.find((value) => value.label === label)?.id || -1;
};

const getPlatformName = (platformId: number, platforms: IPlatform[]) => {
  return getLocalPlatform(platformId, platforms).label;
};

const getPlatformType = (platformId: number, platforms: IPlatform[]) => {
  return getLocalPlatform(platformId, platforms).hrPlatform ? 'hr' : 'customer';
};

const getPlatformByName = async (name: string, platforms?: IPlatform[]) => {
  if (!platforms) {
    platforms = await getPlatforms();
  }

  if (!platforms) return null;

  return platforms.find((value) => value.name.toUpperCase() === name.toUpperCase());
};

const getLocalPlatform = (platformId: number, platforms: IPlatform[]) => {
  const search = platforms.find((v) => platformId === v.id);
  if (!search) {
    console.error('Unknown platform with platformId: ' + platformId);
    return {
      label: 'Unknown platform',
      skipBackdated: false,
      skipBackdatedAbove: -1,
      hrPlatform: false,
    };
  } else {
    return search;
  }
};

const getPlatformID = async (id: number, signal: AbortSignal) => {
  try {
    const { data } = await http.get(`/platforms/${id}`, {});
    return data;
  } catch (error) {
    console.error(error);
  }
};

const getPseudoGoogle = () => {
  return {
    heighest: 5,
    lowest: 1,
    url: '',
    id: -2,
    label: 'google',
    name: 'Google',
    skipBackdated: false,
    skipBackdatedAbove: -1,
    hrPlatform: false,
  };
};

const getOthersPseudoPlatform = (t: (s: string) => string) => {
  return {
    heighest: 0,
    id: -1,
    label: 'others',
    name: t('others'),
    lowest: 0,
    url: '',
    skipBackdated: false,
    skipBackdatedAbove: false,
  };
};

const deletedCanBeOutdated = (platform: string) => {
  return platform == 'trustpilot';
};

const PlatformService = {
  getPlatforms,
  getPlatformId,
  getPlatformID,
  getPlatformName,
  getLocalPlatform,
  getPseudoGoogle,
  deletedCanBeOutdated,
  getOthersPseudoPlatform,
  getPlatformType,
  getPlatformByName,
};

export default PlatformService;
